import {
  CustomFieldDefinition,
  CustomFieldValueInput,
} from '@/graphql/generatedTypes';

/**
 * Build a values map combining default values from definitions
 * with explicit user values (user values take precedence).
 */
export function buildCustomFieldValuesMap(
  definitions: CustomFieldDefinition[],
  values: CustomFieldValueInput[],
): Map<string, string | undefined> {
  const map = new Map<string, string | undefined>();

  // First, populate with default values from definitions
  for (const def of definitions) {
    if (def.default_value) {
      map.set(def.id, def.default_value);
    }
  }

  // Then override with explicit user values
  for (const v of values) {
    map.set(v.id, v.value ?? undefined);
  }

  return map;
}

/**
 * Check if a field should be visible based on its depends_on condition.
 */
export function isCustomFieldVisible(
  field: CustomFieldDefinition,
  valuesMap: Map<string, string | undefined>,
): boolean {
  if (!field.depends_on) return true;
  return valuesMap.get(field.depends_on.field_id) === field.depends_on.value;
}

/**
 * Validate that all visible required custom fields have values.
 */
export function validateRequiredCustomFields(
  definitions: CustomFieldDefinition[],
  values: CustomFieldValueInput[],
): boolean {
  const valuesMap = buildCustomFieldValuesMap(definitions, values);

  const visibleRequiredFields = definitions.filter(
    (f) => f.required && isCustomFieldVisible(f, valuesMap),
  );

  return visibleRequiredFields.every((field) => {
    const value = valuesMap.get(field.id);
    return value !== undefined && value !== '';
  });
}
